import { getFormattedCustomerName } from 'client/components/customers/common/Helpers'
import { ApiError } from 'client/Interfaces/ErrorInterfaces'
import { StateInterface } from 'client/Interfaces/StateInterface'
import { QuotaTypes } from 'client/Interfaces/Subscription'
import { customersSelector } from 'client/reducers/customer'
import { getQuotaInfo } from 'client/reducers/Subscription/companySubscription'
import { Dispatch } from 'redux'

import * as api from '../api/customer'
import { getCustomerForSearchedText } from '../api/customer'
import { UNKNOWN_VALUE } from '../Global/EInvoicingData'
import { CollectionInterface } from '../Interfaces/CollectionInterface'
import { CustomerCollectionInterface, CustomerInterface } from '../Interfaces/CustomerInterface'
import { Immutable } from '../Other/Immutable'
import { actionCreatorAsync, AsyncAction } from './'
import { CustomerActions } from './actionTypes'
import { assignReceiver } from './draft'
import { refreshQuota } from './subscriptionManagement'

export enum CustomerType {
  COMPANY = 'c',
  PRIVATE = 'p',
}

export const customerDraft: CustomerInterface = {
  id: null,
  customerNumber: 0,
  companyName: '',
  address: {
    streetAddress1: '',
    streetAddress2: '',
    pobox: '',
    city: '',
    country: '',
  },
  shippingAddress: {
    streetAddress1: '',
    streetAddress2: '',
    pobox: '',
    city: '',
    country: '',
  },
  firstName: '',
  lastName: '',
  businessId: '',
  customerType: CustomerType.COMPANY,
  name: '',
  displayName: '',
  email: '',
  emails: [],
  vatNumber: '',
  phoneNumberHome: '',
  phoneNumberWork: '',
  title: '',
  paymentTerm: 0,
  language: '',
  vatPct: 0,
  eInvoiceAddress: {
    address: '',
    scheme: null,
  },
  isPublicEntity: false,
  externalContactId: '',
}

export function getCustomerById(id: number): AsyncAction<CustomerInterface> {
  return actionCreatorAsync<CustomerInterface>(CustomerActions.GET_CUSTOMER_BY_ID, async () => api.getCustomerById(id))
}

// get all customers
export function getAllCustomers(fetchFromNetwork = true) {
  return (dispatch: Dispatch, getState: () => StateInterface) => {
    const customers = customersSelector(getState())
    if (!fetchFromNetwork && customers.loaded) {
      return
    }
    dispatch(refreshQuota())
    const info = getQuotaInfo(getState(), QuotaTypes.CUSTOMERS)
    return dispatch(
      actionCreatorAsync<CollectionInterface<CustomerInterface>>(CustomerActions.GET_ALL_CUSTOMERS, async () => {
        const pageable = { page: 1, size: 500, searchTerm: '' }
        const customers = [] as CustomerCollectionInterface[]
        let pageCount
        let totalCount
        do {
          const result = await api.getCustomers(pageable)
          pageCount = result.headers['x-page-count']
          totalCount = result.headers['x-total-count']
          customers.push(...result?.data)
          pageable.page += 1
        } while (pageCount >= pageable.page)
        return {
          quotaInfo: info,
          collection: customers.map(mapLegacyCustomer),
          loaded: true,
          page: pageable?.page,
          pageCount,
          totalCount,
        }
      }),
    )
  }
}

// get customers by pages
export function getCustomers(fetchFromNetwork = true, pageable?: { page: number; size: number; searchTerm: string }) {
  return (dispatch: Dispatch, getState: () => StateInterface) => {
    const customers = customersSelector(getState())
    if (!fetchFromNetwork && customers.loaded) {
      return
    }
    dispatch(refreshQuota())
    const info = getQuotaInfo(getState(), QuotaTypes.CUSTOMERS)
    return dispatch(
      actionCreatorAsync<CollectionInterface<CustomerInterface>>(CustomerActions.GET_CUSTOMERS, async () => {
        const terms = String(pageable?.searchTerm)
          ?.split(/[ ]+/)
          ?.filter((t) => t)
          ?.map((t) => t?.toLowerCase())
        let result, pageCount, totalCount, customers
        if (terms.length <= 1) {
          result = await api.getCustomers(pageable)
          pageCount = result.headers['x-page-count']
          totalCount = result.headers['x-total-count']
          customers = result?.data as CustomerCollectionInterface[]
        } else {
          customers = await searchOlderCustomersForMatchingSearchedText(pageable?.searchTerm)
          totalCount = 0
        }

        return {
          quotaInfo: info,
          collection: customers.map(mapLegacyCustomer),
          loaded: true,
          page: pageable?.page,
          pageCount,
          totalCount,
        }
      }),
    )
  }
}

export function resetCustomer() {
  const type = CustomerActions.RESET_CUSTOMER
  return { type, payload: null }
}

export const mapLegacyCustomer = (customer): CustomerInterface => {
  const mappedCustomer = Immutable.copy(customerDraft)
  for (const key in customerDraft) {
    if (key === 'address') {
      for (const addressKey in customerDraft.address) {
        mappedCustomer.address[addressKey] = customer?.address[addressKey] || customerDraft?.address[addressKey]
      }
    } else if (key === 'eInvoiceAddress') {
      mappedCustomer.eInvoiceAddress = Immutable.copy(customer.eInvoiceAddress || customerDraft.eInvoiceAddress)
    } else if (key === 'shippingAddress') {
      mappedCustomer.shippingAddress = Immutable.copy(customer.shippingAddress || customerDraft.shippingAddress)
    } else {
      mappedCustomer[key] = customer[key] || customerDraft[key]
    }
  }

  mappedCustomer.displayName = getFormattedCustomerName(mappedCustomer, false)
  return mappedCustomer
}

export function createCustomer(customer: CustomerInterface): AsyncAction<CustomerInterface> {
  return actionCreatorAsync<CustomerInterface>(
    CustomerActions.CREATE_CUSTOMER,
    async () => {
      const newCustomer = await api.createCustomer(cleanupCustomerBeforeSave(customer))
      return mapLegacyCustomer(newCustomer)
    },
    (error: ApiError) => {
      const type = CustomerActions.CREATE_CUSTOMER_FAILED
      return { type, payload: error }
    },
  )
}

export function updateCustomer(customer: CustomerInterface): AsyncAction<CustomerInterface> {
  return actionCreatorAsync<CustomerInterface>(
    CustomerActions.UPDATE_CUSTOMER,
    async () => {
      const updatedCustomer = await api.updateCustomer(cleanupCustomerBeforeSave(customer))
      return mapLegacyCustomer(updatedCustomer)
    },
    (error: ApiError) => {
      const type = CustomerActions.UPDATE_CUSTOMER_FAILED
      return { type, payload: error }
    },
  )
}

export function deleteCustomer(id: number): AsyncAction<number> {
  return actionCreatorAsync<number>(CustomerActions.DELETE_CUSTOMER, async () => api.deleteCustomer(id))
}

export const setReceiver = (customer: CustomerInterface) => {
  return (dispatch, getState) => {
    const chosenCustomer = getState().customers.collection?.find((c) => c?.id === customer?.id)
    dispatch(assignReceiver(Immutable.copy(chosenCustomer)))
  }
}

export const setReceiverToNewCustomer = () => {
  return (dispatch, getState) => {
    const { customers } = getState()
    const { lastTouchedId = null } = customers
    const chosenCustomer = customers?.collection?.find((c) => c?.id === lastTouchedId) || null
    dispatch(assignReceiver(Immutable.copy(chosenCustomer)))
  }
}

const cleanupCustomerBeforeSave = (customer: CustomerInterface): CustomerInterface => {
  const customerToSave = Immutable.copy(customer)
  delete customerToSave.displayName
  delete customerToSave.name
  if (customerToSave.eInvoiceAddress && !customerToSave.eInvoiceAddress.scheme) {
    delete customerToSave.eInvoiceAddress
  }
  if (customerToSave.eInvoiceAddress && customerToSave.eInvoiceAddress.networkOperatorId === UNKNOWN_VALUE) {
    customerToSave.eInvoiceAddress.networkOperatorId = null
  }
  if (customerToSave.eInvoiceAddress && customerToSave.eInvoiceAddress.networkType === UNKNOWN_VALUE) {
    customerToSave.eInvoiceAddress.networkType = null
  }
  Object.keys(customerToSave).forEach((k) => {
    if (customerToSave[k] === '') {
      customerToSave[k] = null
    }
  })
  return customerToSave
}

export const getFilteredCustomers = (customers: CustomerInterface[], searchText: string) => {
  let customerData = customers
  const terms = String(searchText)
    ?.split(/[ ]+/)
    .filter((t) => t)
    .map((t) => t.toLowerCase())
  const textLowercase = searchText.toLowerCase()
  if (terms?.length > 1) {
    customerData = customerData?.filter(
      (c) =>
        `${c?.firstName} ${c?.lastName}`?.toLowerCase()?.includes(textLowercase) ||
        c?.companyName?.toLowerCase()?.includes(textLowercase) ||
        c?.firstName?.toLowerCase()?.includes(textLowercase) ||
        c?.lastName?.toLowerCase()?.includes(textLowercase),
    )
    return customerData?.filter((value, index, self) => index === self?.findIndex((t) => t?.id === value?.id))
  } else {
    return customerData
  }
}

const searchOlderCustomersForMatchingSearchedText = async (searchText: string): Promise<CustomerInterface[]> => {
  let customers = [] as CustomerInterface[]
  const terms = String(searchText)
    ?.split(/[ ]+/)
    .filter((t) => t)
    .map((t) => t.toLowerCase())
  await Promise.all(terms?.map((term) => getCustomerForSearchedText(term)))?.then((result) => {
    customers = getFilteredCustomers(result?.flat(), searchText)
  })
  return customers
}
